import { useMemo, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { useReport } from "../../context/report";

export const QuebrasEngomagemTecelagem = () => {
    const { data } = useReport();

    const [buscaGlobal, setBuscaGlobal] = useState('');
    const [statusFiltro, setStatusFiltro] = useState('todos');
    const [operadorFiltro, setOperadorFiltro] = useState(null); 

    const numeroLote = (lote) => {
        if (!lote) return 0;
        const partes = String(lote).split('-').map(p => parseInt(p, 10) || 0);
        if (partes.length >= 3) {
            return partes[0] * 1000000 + partes[1] * 1000 + partes[2];
        } else if (partes.length === 2) {
            return partes[0] * 1000000 + partes[1] * 1000;
        }
        return partes[0] || 0;
    };

    const dadosTabela = useMemo(() => {
        if (!data?.dados) return [];

        const dados = data.dados.flatMap((grupo) =>
            grupo.itens.map((item) => ({
                ...item,
                grupo: grupo.grupo,
                eficiencia: grupo.eficiencia,
                media_quebras: grupo.media_quebras,
                media_quebras_loomgate: grupo.media_quebras_loomgate
            }))
        );

        const ultimoLotePorTear = {};

        dados.forEach((item) => {
            const tear = item.cod_tear;
            const lote = numeroLote(item.lote);

            if (ultimoLotePorTear[tear] === undefined || lote > ultimoLotePorTear[tear]) {
                ultimoLotePorTear[tear] = lote;
            }
        });

        return dados.map((item) => {
            const ultimoLote = ultimoLotePorTear[item.cod_tear];
            const loteAtual = numeroLote(item.lote);
            return {
                ...item,
                concluido: loteAtual !== ultimoLote
            };
        });
    }, [data]);

    const operadorOptions = useMemo(() => {
        const operadoresUnicos = [...new Set(dadosTabela.map(item => item.operador))].filter(Boolean);
        operadoresUnicos.sort();
        
        return [
            { label: 'Todos os Operadores', value: null },
            ...operadoresUnicos.map(op => ({ label: op, value: op }))
        ];
    }, [dadosTabela]);

    const statusOptions = [
        { label: 'Todos', value: 'todos' },
        { label: 'Em produção', value: 'producao' },
        { label: 'Concluídos', value: 'concluido' }
    ];

    const dadosFiltrados = useMemo(() => {
        let resultado = dadosTabela;

        if (statusFiltro === 'producao') {
            resultado = resultado.filter((item) => item.concluido === false);
        } else if (statusFiltro === 'concluido') {
            resultado = resultado.filter((item) => item.concluido === true);
        }

        if (operadorFiltro !== null && operadorFiltro !== undefined && operadorFiltro !== '') {
            resultado = resultado.filter((item) => item.operador === operadorFiltro);
        }

        return resultado;
    }, [dadosTabela, statusFiltro, operadorFiltro]);

    const formatNumber = (rowData, props) => {
        const valor = rowData[props.field];
        if (valor === null || valor === undefined) return '-';
        return Intl.NumberFormat('pt-BR', { maximumFractionDigits: 2 }).format(valor);
    };

    const statusFilterTemplate = () => (
        <Dropdown
            value={statusFiltro}
            options={statusOptions}
            onChange={(e) => setStatusFiltro(e.value)}
            placeholder="Todos"
            style={{ width: '100%' }}
        />
    );

    const operadorFilterTemplate = () => (
        <Dropdown
            value={operadorFiltro}
            options={operadorOptions}
            onChange={(e) => setOperadorFiltro(e.value)}
            placeholder="Selecione..."
            style={{ width: '100%' }}
            filter
            showClear
        />
    );

    const statusBodyTemplate = (rowData) => {
        return rowData.concluido ? 'CONCLUÍDO' : 'EM PRODUÇÃO';
    };

    return (
        <div>
            <h3>{data?.periodo}</h3>

            <div
                className="p-d-flex p-jc-between p-ai-start p-mb-3"
                style={{ flexWrap: 'wrap', gap: '20px' }}
            >
                <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap', flex: 1 }}>
                    {data?.dados && data.dados.length > 0 ? (
                        data.dados.map((grupo) => (
                            <div 
                                key={grupo.grupo} 
                                style={{ backgroundColor: '#f8f9fa', padding: '15px', borderRadius: '8px', border: '1px solid #dee2e6', minWidth: '220px' }}
                            >
                                <h4 style={{ margin: '0 0 10px 0', color: '#495057' }}>Grupo {grupo.grupo}</h4>
                                <div style={{ fontSize: '14px', lineHeight: '1.6', color: '#495057' }}>
                                    <div><strong>Eficiência:</strong> {grupo.eficiencia}%</div>
                                    <div><strong>Média Quebras:</strong> {grupo.media_quebras}</div>
                                    <div><strong>Média Quebras Loomgate:</strong> {grupo.media_quebras_loomgate}</div>
                                </div>
                            </div>
                        ))
                    ) : (
                        <h4 style={{ margin: 0, color: '#6c757d' }}>Nenhum dado retornado para a busca.</h4>
                    )}
                </div>

                <div>
                    <span className="p-input-icon-left">
                        <i className="pi pi-search" />
                        <InputText
                            aria-label="Busca global"
                            onChange={(event) => setBuscaGlobal(event.target.value)}
                            placeholder="Buscar em todos os campos"
                            type="search"
                            value={buscaGlobal}
                        />
                    </span>
                </div>
            </div>

            <DataTable
                emptyMessage="Nenhum registro encontrado."
                globalFilter={buscaGlobal}
                globalFilterFields={[
                    'grupo', 'cod_tear', 'lote', 'operador', 'rolo', 
                    'cod_artigo', 'quebras', 'quebras_loomgate', 'concluido'
                ]}
                responsiveLayout="scroll"
                size="small"
                value={dadosFiltrados}
                filterDisplay="row"
            >
                <Column align="center" header="GRUPO" field="grupo" sortable />
                <Column align="center" header="STATUS" field="concluido" filter filterElement={statusFilterTemplate} showFilterMenu={false} sortable body={statusBodyTemplate} />
                <Column align="center" header="TEAR" field="cod_tear" filter filterPlaceholder="Filtrar Tear..." showFilterMenu={false} sortable />
                <Column align="center" header="LOTE" field="lote" sortable />
                <Column align="center" header="OPERADOR" field="operador" filter filterElement={operadorFilterTemplate} showFilterMenu={false} sortable />
                <Column align="center" header="OLO" field="rolo" sortable />
                <Column align="center" header="ARTIGO" field="cod_artigo" filter filterPlaceholder="Filtrar Artigo..." showFilterMenu={false} sortable />
                <Column
                    align="center"
                    header="QUEBRAS SPI"
                    field="quebras"
                    sortable
                    body={(rowData) => (
                        <div style={{ backgroundColor: rowData.cor, margin: 0, padding: 4, width: '100%' }}>
                            <span>{rowData.quebras}</span>
                        </div>
                    )}
                />
                <Column align="center" header="QUEBRAS LOOMGATE" field="quebras_loomgate" body={formatNumber} sortable />
            </DataTable>
        </div>
    );
};